import React, { useState } from 'react';
import { BookOpen, ArrowUpRight, ChevronDown } from 'lucide-react';
import { Reveal } from '../components/Reveal';
import PdfFlipbook from '../components/PdfFlipbook';
import IssueCarousel from '../components/IssueCarousel';
import { useContent } from '../context/ContentContext';

export default function Editions() {
  const { editions, digitalEditionUrl } = useContent();
  const list = editions.length ? editions : [];
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);
  const current = list[idx];
  const hasPdf = current && current.pdfUrl;
  const readUrl = current ? (current.readUrl || digitalEditionUrl) : digitalEditionUrl;

  return (
    <div className="pt-32 md:pt-40 pb-24">
      <div className="section-pad">
        <div className="max-w-[1500px] mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <p className="eyebrow text-[#D01919] mb-4">The E-Magazine</p>
                <h1 className="font-display text-6xl md:text-8xl leading-none">Read every issue.</h1>
                <p className="text-[#666666] text-lg mt-6 max-w-xl">Flip through the latest edition or dive into the archive — every Enersider issue in one place.</p>
              </div>
              {/* Issue picker */}
              {list.length > 0 && (
                <div className="relative">
                  <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-3 bg-[#f4f4f4] rounded-full pl-5 pr-4 py-3 min-w-[240px] justify-between hover:bg-[#e5e5e5] transition-colors">
                    <span className="font-medium">{current?.month} — {current?.title}</span>
                    <ChevronDown size={18} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="absolute right-0 mt-2 w-full min-w-[280px] bg-white rounded-2xl shadow-xl border border-[#e5e5e5] py-2 z-30 max-h-80 overflow-auto">
                      {list.map((e, i) => (
                        <button key={e.id || i} onClick={() => { setIdx(i); setOpen(false); }} className={`w-full text-left px-5 py-3 hover:bg-[#f4f4f4] transition-colors ${i === idx ? 'text-[#D01919] font-semibold' : ''}`}>
                          <span className="block text-sm font-medium">{e.month}</span>
                          <span className="block text-xs text-[#666666]">{e.title}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Reveal>

          {current && (
            <Reveal delay={100}>
              <div className="mt-12 grid lg:grid-cols-3 gap-10">
                {/* Cover + meta */}
                <div className="lg:col-span-1">
                  <div className="rounded-2xl overflow-hidden shadow-xl aspect-[3/4]">
                    <img src={current.cover} alt={current.title} className="w-full h-full object-cover" />
                  </div>
                  <p className="eyebrow text-[#D01919] mt-5">{current.month}</p>
                  <h2 className="font-display text-3xl mt-1">{current.title}</h2>
                  <p className="text-[#666666] mt-3">{current.summary}</p>
                  <a href={hasPdf ? current.pdfUrl : readUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 bg-[#111111] text-white font-medium px-6 py-3.5 rounded-full hover:bg-[#D01919] transition-colors">
                    <BookOpen size={18} /> {hasPdf ? 'Open full PDF' : 'Open the flipbook'}
                  </a>
                </div>
                {/* Reader: in-app flip reader for uploaded PDFs, else embedded external flipbook */}
                <div className="lg:col-span-2">
                  {hasPdf ? (
                    <div className="rounded-2xl border border-[#e5e5e5] bg-[#f4f4f4] p-4 md:p-6">
                      <PdfFlipbook url={current.pdfUrl} />
                    </div>
                  ) : (
                    <>
                      <div className="relative w-full rounded-2xl overflow-hidden border border-[#e5e5e5] bg-[#f4f4f4]" style={{ paddingBottom: '70%' }}>
                        <iframe title={current.title} src={readUrl} className="absolute inset-0 w-full h-full" frameBorder="0" allowFullScreen />
                      </div>
                      <p className="text-xs text-[#666666] mt-3">Tip: upload a PDF for this issue in the admin to use the built-in flip reader. If an embedded host blocks loading, use "Open the flipbook".</p>
                    </>
                  )}
                </div>
              </div>
            </Reveal>
          )}

          {/* Archive grid */}
          {list.length > 1 && (
            <div className="mt-20">
              <h3 className="font-display text-3xl md:text-4xl mb-8">The archive</h3>
              <IssueCarousel items={list} testid="editions-archive" renderItem={(e, i) => (
                <button onClick={() => { setIdx(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="group text-left w-full" data-testid={`editions-archive-item-${i}`}>
                  <div className="rounded-2xl overflow-hidden aspect-[3/4] mb-3"><img src={e.cover} alt={e.title} draggable={false} className="card-img w-full h-full object-cover" /></div>
                  <p className="eyebrow text-[#666666]">{e.month}</p>
                  <h4 className="font-display text-xl mt-1 group-hover:text-[#D01919] transition-colors">{e.title}</h4>
                </button>
              )} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
